'use client';

import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';
import pricingData from './pricing-data.json';

// Generate comparison map from pricing data JSON
const generateComparisonMap = () => {
  // Get the plans we want to show in the comparison (excluding free_user)
  const comparisonPlans = pricingData.plans.filter(
    plan => plan.publicly_available && plan.slug !== 'free_user'
  );

  const planSlugs = comparisonPlans.map(plan => plan.slug);

  const comparisonMap: { [key: string]: boolean[] } = {};

  // Process each feature
  pricingData.features.forEach(feature => {
    if (feature.publicly_available) {
      // Check which plans include this feature
      const featureAvailability = planSlugs.map(planSlug =>
        feature.plan_slugs.includes(planSlug)
      );

      comparisonMap[feature.name] = featureAvailability;
    }
  });

  return comparisonMap;
};

export const detailedComparisonMap = generateComparisonMap();

const Row = ({ feature, values }: { feature: string; values: boolean[] }) => {
  return (
    <div className="grid grid-cols-4 gap-x-16 border-b border-border px-4 py-5 text-body-dense text-foreground">
      <div className="text-muted-foreground">{feature}</div>
      {values.map((value: boolean, index: number) =>
        value ? (
          <div
            key={`${feature}-${index}`}
            className="flex items-center justify-center"
          >
            <Check className="size-5 text-status-success" />
          </div>
        ) : (
          <div
            key={`${feature}-${index}`}
            className="flex items-center justify-center"
          >
            <X className="size-5 text-muted-foreground" />
          </div>
        )
      )}
    </div>
  );
};

const PricingFeatureTable = () => {
  const [showDetailedComparison, setShowDetailedComparison] = useState(false);

  const head = (
    <div className="grid grid-cols-4 gap-x-16 border-t border-b border-border bg-muted p-4 text-label text-foreground">
      {/* First div is intentionally empty */}
      <div></div>
      {pricingData.plans.map(
        plan =>
          plan.publicly_available && (
            <div key={plan.slug} className="flex justify-center">
              {plan.name}
            </div>
          )
      )}
    </div>
  );

  const rows = (
    <>
      {Object.entries(detailedComparisonMap).map(([feature, values]) => (
        <Row key={feature} feature={feature} values={values as boolean[]} />
      ))}
    </>
  );

  const table = (
    <div
      id="detailed-pricing"
      className={`${
        showDetailedComparison ? 'block' : 'hidden'
      } overflow-x-auto mt-8 w-full`}
    >
      <div className="overflow-hidden min-w-max">
        {head}
        {rows}
      </div>
    </div>
  );

  const trigger = (
    <div className="mt-8">
      <button
        type="button"
        className="mx-auto flex min-h-11 items-center justify-center text-label text-secondary-foreground hover:text-foreground"
        onClick={() => setShowDetailedComparison(!showDetailedComparison)}
      >
        Show Detailed Plan Comparison{' '}
        {showDetailedComparison ? (
          <ChevronUp className="ml-1 size-4" />
        ) : (
          <ChevronDown className="ml-1 size-4" />
        )}
      </button>
    </div>
  );

  return (
    <>
      {trigger}
      {table}
    </>
  );
};

export default PricingFeatureTable;
